import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatRupiah } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Clock, QrCode, Package, MessageCircle, Store, FileText } from "lucide-react";

const QRIS_IMAGE = "https://customer-assets-0z36b82j.emergentagent.net/job_order-printing-id/artifacts/2rfvayxj_image.png";
const WA_PHONE = "6282227132011";
const MERCHANT = {
  name: "Rooftop Print",
  address: "Unit, Jl. Pagarsih No.122, Cibadak, Kec. Astanaanyar, Kota Bandung, Jawa Barat 40241",
  nmid: "ID1025380267209",
};

export default function Payment() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [autoPayEnabled, setAutoPayEnabled] = useState(false);

  useEffect(() => {
    api.get("/payments/config")
      .then(({ data }) => setAutoPayEnabled(!!data.enabled))
      .catch(() => setAutoPayEnabled(false));
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/orders/${orderId}`);
      setOrder(res.data);
    } catch (e) {
      toast.error("Pesanan tidak ditemukan");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!order || order.payment_status === "paid") return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [order, load]);

  const loadSnap = (jsUrl, clientKey) => new Promise((resolve, reject) => {
    if (window.snap) return resolve();
    const s = document.createElement("script");
    s.src = jsUrl; s.setAttribute("data-client-key", clientKey);
    s.onload = () => resolve(); s.onerror = () => reject(new Error("snap load failed"));
    document.body.appendChild(s);
  });
  const payNow = async () => {
    try {
      setPaying(true);
      const { data } = await api.post(`/orders/${orderId}/snap-token`);
      await loadSnap(data.snap_js_url, data.client_key);
      window.snap.pay(data.token, {
        onSuccess: () => { toast.success("Pembayaran diterima!"); load(); },
        onPending: () => { toast.info("Menunggu pembayaran diselesaikan"); load(); },
        onError: () => toast.error("Pembayaran gagal. Coba lagi."),
        onClose: () => toast.info("Jendela pembayaran ditutup. Pesanan tetap menunggu pembayaran."),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memulai pembayaran otomatis");
    } finally { setPaying(false); }
  };

  if (loading) return <div className="App"><Navbar /><div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#F0005A]" /></div></div>;
  if (!order) return <div className="App"><Navbar /><div className="text-center py-24">Pesanan tidak ditemukan</div></div>;

  const paid = order.payment_status === "paid";
  const waMessage = `Halo Rooftop Print, saya ingin konfirmasi pembayaran pesanan #${order.order_number} dengan total ${formatRupiah(order.total)}. Berikut saya lampirkan bukti transfernya.`;
  const waHref = `https://wa.me/${WA_PHONE}?text=${encodeURIComponent(waMessage)}`;

  return (
    <div className="App">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <p className="text-sm text-slate-500 uppercase tracking-wider">Pesanan Berhasil Dibuat</p>
          <h1 className="font-heading text-3xl font-bold text-[#00133F]" data-testid="order-number">#{order.order_number}</h1>
        </div>

        <div className="mb-6 rounded-xl bg-slate-50 border border-slate-200 p-4 flex items-start gap-2" data-testid="large-file-instruction">
          <FileText className="w-4 h-4 text-[#00133F] shrink-0 mt-0.5" />
          <p className="text-sm text-slate-600 leading-relaxed">Untuk file desain berukuran besar (di atas 10MB) seperti PDF, PSD, AI, atau CDR, mohon kirimkan ke email kami: <a href={`mailto:rooftopprint@gmail.com?subject=Desain%20Pesanan%20%23${order.order_number}`} className="text-[#F0005A] font-medium">rooftopprint@gmail.com</a> dengan mencantumkan Nomor Pesanan (<span className="font-semibold">#{order.order_number}</span>) di subjek email.</p>
        </div>

        {paid ? (
          <div className="rounded-2xl border border-[#10B981]/30 bg-[#10B981]/5 p-10 text-center" data-testid="payment-success">
            <div className="w-16 h-16 rounded-full bg-[#10B981] flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-[#00133F]">Pembayaran Terkonfirmasi</h2>
            <p className="text-slate-600 mt-2">Terima kasih! Pesananmu sedang kami proses.</p>
            <div className="flex gap-3 justify-center mt-6">
              <Link to="/pesanan"><Button className="rounded-full bg-[#00133F] text-white" data-testid="view-orders-btn"><Package className="w-4 h-4 mr-2" /> Lihat Pesanan</Button></Link>
              <Link to="/produk"><Button variant="outline" className="rounded-full">Belanja Lagi</Button></Link>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="bg-[#00133F] text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <QrCode className="w-5 h-5 text-[#F0005A]" />
                <span className="font-heading font-semibold">Pembayaran via QRIS</span>
              </div>
              <span className="flex items-center gap-1.5 text-xs text-amber-300"><Clock className="w-4 h-4" /> Menunggu pembayaran</span>
            </div>

            <div className="p-6">
              <div className="text-center mb-6">
                <p className="text-sm text-slate-500">Total Pembayaran</p>
                <p className="font-heading text-3xl font-bold text-[#F0005A]" data-testid="payment-amount">{formatRupiah(order.total)}</p>
              </div>

              {/* Midtrans automatic payment */}
              {autoPayEnabled && (
                <>
                  <div className="rounded-xl border border-[#00133F]/15 bg-[#00133F]/5 p-4 mb-6">
                    <p className="text-sm font-semibold text-[#00133F] text-center mb-1">Bayar Otomatis (QRIS Dinamis / Virtual Account)</p>
                    <p className="text-xs text-slate-500 text-center mb-3">QRIS nominal otomatis, atau VA BCA / Mandiri / BRI / BNI. Status langsung terverifikasi.</p>
                    <Button onClick={payNow} disabled={paying} data-testid="pay-now-btn"
                      className="w-full h-12 rounded-full bg-[#F0005A] hover:brightness-110 active:scale-95 transition-transform text-white text-base disabled:opacity-50">
                      {paying ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Memuat pembayaran...</> : <><QrCode className="w-5 h-5 mr-2" /> Bayar Sekarang</>}
                    </Button>
                  </div>

                  <div className="relative text-center my-5"><span className="text-xs text-slate-400 bg-white px-3">atau bayar manual via QRIS statis</span><div className="absolute inset-0 top-1/2 border-t border-slate-100 -z-0" /></div>
                </>
              )}

              {/* Merchant info */}
              <div className="flex items-center gap-2 justify-center text-center mb-4">
                <Store className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="font-semibold text-[#00133F] text-sm">{MERCHANT.name}</p>
                  <p className="text-[11px] text-slate-400">NMID: {MERCHANT.nmid}</p>
                </div>
              </div>

              {/* Static QRIS image */}
              <div className="flex flex-col items-center">
                <div className="p-3 rounded-2xl border border-slate-200 bg-white">
                  <img src={QRIS_IMAGE} alt="QRIS Rooftop Print" className="w-72 max-w-full h-auto rounded-lg" data-testid="qris-image" />
                </div>
              </div>

              {/* Instruction */}
              <div className="mt-6 rounded-xl bg-[#F0005A]/5 border border-[#F0005A]/20 p-4 text-center">
                <p className="text-sm text-[#00133F] font-medium" data-testid="payment-instruction">
                  Silakan scan QRIS di atas dan kirim bukti transfer ke WhatsApp kami untuk konfirmasi pesanan.
                </p>
              </div>

              {/* WhatsApp confirm button */}
              <div className="mt-5">
                <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="confirm-whatsapp-btn">
                  <Button className="w-full h-12 rounded-full bg-[#25D366] hover:brightness-105 active:scale-95 transition-transform text-white text-base">
                    <MessageCircle className="w-5 h-5 mr-2" /> Konfirmasi via WhatsApp
                  </Button>
                </a>
                <p className="text-xs text-center text-slate-400 mt-3">
                  Pesananmu akan diproses setelah bukti transfer dikonfirmasi oleh tim kami.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Order items */}
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="font-heading font-semibold text-[#00133F] mb-4">Rincian Pesanan</h3>
          <div className="space-y-3">
            {order.items.map((it, idx) => (
              <div key={idx} className="flex gap-3 items-center">
                <div className="relative w-12 h-12 rounded-lg overflow-hidden bg-slate-50 shrink-0">
                  <img src={it.image_url} alt="" className="w-full h-full object-cover" />
                  {it.design_file_id && <img src={`${api.defaults.baseURL}/files/${it.design_file_id}`} alt="" className="absolute inset-0 m-auto w-8 h-8 object-cover rounded shadow" />}
                </div>
                <div className="flex-1 text-sm"><p className="font-medium text-[#00133F]">{it.product_name}{(it.variant_selections||[]).length ? " · " + it.variant_selections.map((v)=>v.option_label).join(", ") : ""}</p><p className="text-slate-400 text-xs">{it.quantity} × {formatRupiah(it.unit_price)}{it.product_kind === "custom" && !it.design_file_id ? " · desain via WA" : ""}</p></div>
                <span className="text-sm font-medium">{formatRupiah(it.line_total)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 mt-4 pt-4 space-y-2 text-sm">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{formatRupiah(order.subtotal)}</span></div>
            <div className="flex justify-between text-slate-600"><span>Ongkir ({order.shipping.courier} {order.shipping.service})</span><span>{formatRupiah(order.shipping_cost)}</span></div>
            <div className="flex justify-between font-bold text-[#00133F] pt-2 border-t border-slate-100"><span>Total</span><span>{formatRupiah(order.total)}</span></div>
          </div>
          <div className="border-t border-slate-100 mt-4 pt-4 text-sm text-slate-500">
            <p className="font-medium text-[#00133F] mb-1">Dikirim ke:</p>
            <p>{order.shipping_address.recipient_name} · {order.shipping_address.phone}</p>
            <p>{order.shipping_address.address}, {order.shipping_address.city}, {order.shipping_address.province} {order.shipping_address.postal_code}</p>
            {order.tracking_number && (
              <p className="mt-2 text-[#00133F]" data-testid="payment-tracking">No. Resi: <span className="font-mono font-semibold">{order.tracking_number}</span> ({order.shipping.courier} {order.shipping.service})</p>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
